(ns machtfit.subsidy-rate
  (:require [cljs-time.coerce :as time.coerce]
            [cljs-time.core :as time]
            [clojure.string :as str]
            [machtfit.company-editor-styles :as styles]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.subsidy-rate.options :as opts]
            [machtfit.utils :as utils]
            [machtfit.widgets.checkbox :as checkbox]
            [machtfit.widgets.dropdown :as dropdown]
            [re-frame.core :as rf]))

(def legal-cap-for-monthly-sachbezug 50)

(defn ^:export load [js-data]
  (rf/dispatch [::init (js->clj js-data :keywordize-keys true)]))

(rf/reg-event-db ::init
  (fn [db [_ data]]
    (merge db data)))

(rf/reg-sub ::rule-by-index
  (fn [db [_ index]]
    (get-in db [:data :rules index])))

(rf/reg-sub ::rule-condition-values
  :<- [:get [:data :rules]]

  (fn [rules [_ prop]]
    (remove nil? (for [rule rules] (get-in rule [:condition :employee prop])))))

(rf/reg-sub ::options-occupation-groups
  :<- [:get [:data :occupation-groups]]
  :<- [::rule-condition-values :occupation-group]

  (fn [[options options-from-rules] _]
    (sort (distinct (into options options-from-rules)))))

(rf/reg-sub ::options-subsidiaries
  :<- [:get [:data :subsidiaries]]
  :<- [::rule-condition-values :subsidiary]

  (fn [[options options-from-rules] _]
    (sort (distinct (into options options-from-rules)))))

(rf/reg-sub ::employee-group-by-id
  :<- [:get [:data :employee-groups]]

  (fn [employee-groups [_ id]]
    (utils/by-id employee-groups id)))

(rf/reg-sub ::options-service-formats
  :<- [:get [:data :service-formats]]

  (fn [service-formats [_ delivery-type-slug]]
    (opts/service-formats-for-delivery-type service-formats delivery-type-slug)))

(rf/reg-sub ::options-health-categories
  :<- [:get [:data :health-categories]]

  (fn [health-categories [_ domain-slug]]
    (opts/health-categories-for-domain health-categories domain-slug)))

(rf/reg-sub ::titles-for-slugs
  (fn [db [_ data-key value]]
    (let [slugs (if (sequential? value)
                  value
                  [value])
          options (get-in db [:data data-key])
          title-by-slug (into {} (map (juxt :slug :title)) options)]
      (->> slugs
           (keep title-by-slug)
           (str/join ", ")))))

(rf/reg-sub ::num-rules
  :<- [:get [:data :rules]]

  (fn [rules _]
    (count rules)))

(rf/reg-event-db ::move-rule-up
  (fn [db [_ index]]
    (-> db
        (update-in [:data :rules] utils/move-up index)
        (update-in [:ui :editing :rule] utils/decrease-index))))

(rf/reg-event-db ::move-rule-down
  (fn [db [_ index]]
    (-> db
        (update-in [:data :rules] utils/move-down index)
        (update-in [:ui :editing :rule] utils/increase-index))))

(rf/reg-event-db ::add-rule
  (fn [db _]
    (let [new-rule {:result 0 :condition {:product {:is_estg3no34_subsidizable false}}}
          add-rule #(conj (if (empty? %) [] %) new-rule)]
      (-> db
          (update-in [:data :rules] add-rule)
          (assoc-in [:ui :editing] {:rule (count (-> db :data :rules))})))))

(rf/reg-event-db ::edit-rule
  (fn [db [_ index]]
    (assoc-in db [:ui :editing] {:rule index})))

(rf/reg-event-db ::delete-rule
  (fn [db [_ index]]
    (update-in db [:data :rules] utils/delete-at index)))

(rf/reg-sub ::places-of-business-by-id
  :<- [:get [:data :places-of-business]]

  (fn [places-of-business [_ id]]
    (utils/by-id places-of-business id)))

(rf/reg-sub ::product-by-id
  :<- [:get [:data :products]]

  (fn [products [_ id]]
    (utils/by-id products id)))

(rf/reg-sub ::exists-employee-condition?
  :<- [::rule-condition-values :occupation-group]
  :<- [::rule-condition-values :place-of-business]
  :<- [::rule-condition-values :subsidiary]
  :<- [::rule-condition-values :employee-group]

  (fn [[occupation-groups places-of-business subsidiaries] _]
    (not-every? empty? [occupation-groups places-of-business subsidiaries])))

(defn- rule-conditions-employee [c]
  [:span
   (when (:occupation-group c)
     (str " Berufsgruppe: " (:occupation-group c)))
   (when (:place-of-business c)
     (str " Standort: " (:name @(rf/subscribe [::places-of-business-by-id (:place-of-business c)]))))
   (when (:subsidiary c)
     (str " Unternehmensteil: " (:subsidiary c)))
   (when (:employee-group c)
     (str " Mitarbeitergruppe: " (:name @(rf/subscribe [::employee-group-by-id (:employee-group c)]))))
   (when-not ((some-fn :occupation-group :place-of-business :subsidiary :employee-group) c)
     (str (if @(rf/subscribe [::exists-employee-condition?]) "restliche" "alle") " Mitarbeiter"))])

(def default-value-string "–––")

(def ^:private action-link-classes
  ["cursor-pointer" "text-sm" "font-medium" "text-aquafitblue-700" "transition-colors" "hover:text-aquafitblue-800" "hover:underline"])

(def ^:private percent-input-classes
  ["w-20" "rounded-lg" "border" "border-gray-200" "bg-white" "px-3" "py-2" "text-center" "text-sm"
   "tabular-nums" "text-gray-900" "transition-colors" "focus:border-brand-400" "focus:outline-none"
   "focus:ring" "focus:ring-brand-500/15"])

;; widgets/select-box and native date inputs take no :class; style/width-cap them via descendant variants.
(def ^:private edit-control-variant-classes
  ["[&_select]:max-w-[240px]" "[&_select]:rounded-lg" "[&_select]:border" "[&_select]:border-gray-200" "[&_select]:bg-white"
   "[&_select]:px-2.5" "[&_select]:py-1.5" "[&_select]:text-sm" "[&_select]:text-gray-900"
   "[&_input[type=date]]:rounded-lg" "[&_input[type=date]]:border" "[&_input[type=date]]:border-gray-200"
   "[&_input[type=date]]:bg-white" "[&_input[type=date]]:px-2.5" "[&_input[type=date]]:py-1.5" "[&_input[type=date]]:text-sm"])

(defn- value-label-options [items]
  (map (fn [{:keys [slug title]}]
         {:value slug
          :label title})
       items))

(defn- edit-rule-group [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        condition-path [:data :rules index :condition :employee :occupation-group]]
    [:tr
     [:td "Berufsgruppe:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee :occupation-group) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (if (= default-value-string %) nil %)])
            :data (into [default-value-string] @(rf/subscribe [::options-occupation-groups]))}]]]))

(defn- edit-rule-place-of-business [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        condition-path [:data :rules index :condition :employee :place-of-business]]
    [:tr
     [:td "Standort:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee :place-of-business) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (when (not= default-value-string %)
                                                                 (int %))])
            :data (into [{:value default-value-string :label default-value-string}]
                        (map #(identity {:value (:id %) :label (:name %)})
                             @(rf/subscribe [:get [:data :places-of-business]])))}]]]))

(defn- edit-rule-employee-group [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        condition-path [:data :rules index :condition :employee :employee-group]]
    [:tr
     [:td "Mitarbeitergruppe:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee :employee-group) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (if (= default-value-string %) nil (int %))])
            :data (into [{:value default-value-string :label default-value-string}]
                        (map #(identity {:value (:id %) :label (str (:name %) " (" (:type %) ")")})
                             @(rf/subscribe [:get [:data :employee-groups]])))}]]]))

(rf/reg-fx ::alert
  (fn [text]
    (js/alert text)))

(defn- period-invalid? [start end]
  (and start
       end
       (time/after? (time.coerce/from-string start)
                    (time.coerce/from-string end))))

(defn- update-period [rule-index db start end]
  (let [start (when-not (str/blank? start)
                start)
        end (when-not (str/blank? end)
              end)]
    (if (period-invalid? start end)
      {:db db
       ::alert "Regel muss anfangen bevor sie endet."}
      {:db (-> db
               (assoc-in [:data :rules rule-index :start] start)
               (assoc-in [:data :rules rule-index :end] end))})))

(rf/reg-event-fx ::set-start
  (fn [{:keys [db]} [_ rule-index start]]
    (update-period rule-index db start (get-in db [:data :rules rule-index :end]))))

(rf/reg-event-fx ::set-end
  (fn [{:keys [db]} [_ rule-index end]]
    (update-period rule-index db (get-in db [:data :rules rule-index :start]) end)))

(rf/reg-sub ::start
  (fn [db [_ rule-index]]
    (get-in db [:data :rules rule-index :start])))

(rf/reg-sub ::end
  (fn [db [_ rule-index]]
    (get-in db [:data :rules rule-index :end])))

(defn- edit-rule-subsidiary [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        condition-path [:data :rules index :condition :employee :subsidiary]]
    [:tr
     [:td "Unternehmensteil:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee :subsidiary) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (if (= default-value-string %) nil %)])
            :data (into [default-value-string] @(rf/subscribe [::options-subsidiaries]))}]]]))

(defn- edit-rule-conditions-employee [index]
  [:table>tbody
   [edit-rule-group index]
   [edit-rule-place-of-business index]
   [edit-rule-subsidiary index]
   [edit-rule-employee-group index]])

(defn- product-link [product-id]
  (let [product @(rf/subscribe [::product-by-id product-id])]
    [:span>a {:href (:url product) :target "_blank"} (:title product)]))

(defn- rule-conditions-product [c]
  (if-let [product-id (:id c)]
    [product-link product-id]
    [:span " ist" (when-not (:is_estg3no34_subsidizable c) [:em " nicht"]) " bezuschussbar"
     (when (:category c)
       (str " aus Kategorie: " (:category c)))
     (when (:delivery_type c)
       (str " Angebotstyp: " @(rf/subscribe [::titles-for-slugs :delivery-types (:delivery_type c)])))
     (when (seq (:service_format c))
       (str " Leistungsformat: " @(rf/subscribe [::titles-for-slugs :service-formats (:service_format c)])))
     (when (:health_domain c)
       (str " Handlungsfeld: " @(rf/subscribe [::titles-for-slugs :health-domains (:health_domain c)])))
     (when (seq (:health_category c))
       (str " Gesundheitskategorie: " @(rf/subscribe [::titles-for-slugs :health-categories (:health_category c)])))]))

(rf/reg-event-db ::toggle-subsidizable
  (fn [db [_ index]]
    (let [path [:data :rules index :condition :product :is_estg3no34_subsidizable]
          newval (not (get-in db path))]
      (cond-> (assoc-in db path newval)
        (= newval true) (update-in [:data :rules index] #(dissoc % :subsidy-cap))))))

(rf/reg-event-db ::set-delivery-type
  (fn [db [_ index value]]
    (-> db
        (assoc-in [:data :rules index :condition :product :delivery_type] value)
        (update-in [:data :rules index :condition :product] dissoc :service_format))))

(rf/reg-event-db ::set-health-domain
  (fn [db [_ index value]]
    (-> db
        (assoc-in [:data :rules index :condition :product :health_domain] value)
        (update-in [:data :rules index :condition :product] dissoc :health_category))))

(rf/reg-event-db ::toggle-product-slug
  (fn [db [_ index field slug]]
    (let [path [:data :rules index :condition :product field]
          updated (opts/toggle-slug (get-in db path) slug)]
      (if (seq updated)
        (assoc-in db path updated)
        (update-in db [:data :rules index :condition :product] dissoc field)))))

(defn- edit-rule-subsidizable [index]
  (let [rule @(rf/subscribe [::rule-by-index index])]
    [:tr
     [:td "ist bezuschussbar nach § 3 Nr. 34 EStG"]
     [:td [checkbox/checkbox
           {:checked? (-> rule :condition :product :is_estg3no34_subsidizable)
            :on-click #(rf/dispatch [::toggle-subsidizable index])}]]]))

(defn- edit-rule-category [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        condition-path [:data :rules index :condition :product :category]]
    [:tr
     [:td "aus Kategorie: "]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :product :category) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (if (= default-value-string %) nil %)])
            :data (into [default-value-string] @(rf/subscribe [:get [:data :categories]]))}]]]))

(defn- edit-rule-delivery-type [index]
  (let [rule @(rf/subscribe [::rule-by-index index])]
    [:tr
     [:td "Angebotstyp:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :product :delivery_type) default-value-string)
            :on-change #(rf/dispatch [::set-delivery-type index (when (not= default-value-string %) %)])
            :data (into [{:value default-value-string :label default-value-string}]
                        (value-label-options @(rf/subscribe [:get [:data :delivery-types]])))}]]]))

(defn- edit-rule-service-format [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        delivery-type-slug (-> rule :condition :product :delivery_type)]
    [:tr
     [:td "Leistungsformat:"]
     [:td [dropdown/searchable-multi-listbox
           {:options (value-label-options @(rf/subscribe [::options-service-formats delivery-type-slug]))
            :selected (set (get-in rule [:condition :product :service_format]))
            :on-toggle #(rf/dispatch [::toggle-product-slug index :service_format %])}]]]))

(defn- edit-rule-health-domain [index]
  (let [rule @(rf/subscribe [::rule-by-index index])]
    [:tr
     [:td "Handlungsfeld:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :product :health_domain) default-value-string)
            :on-change #(rf/dispatch [::set-health-domain index (when (not= default-value-string %) %)])
            :data (into [{:value default-value-string :label default-value-string}]
                        (value-label-options @(rf/subscribe [:get [:data :health-domains]])))}]]]))

(defn- edit-rule-health-category [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        domain-slug (-> rule :condition :product :health_domain)]
    [:tr
     [:td "Gesundheitskategorie:"]
     [:td [dropdown/searchable-multi-listbox
           {:options (value-label-options @(rf/subscribe [::options-health-categories domain-slug]))
            :selected (set (get-in rule [:condition :product :health_category]))
            :on-toggle #(rf/dispatch [::toggle-product-slug index :health_category %])}]]]))

(defn- edit-rule-conditions-product [index product-id]
  [:table>tbody
   (if product-id
     [product-link product-id]
     [:<>
      [edit-rule-subsidizable index]
      [edit-rule-category index]
      [edit-rule-delivery-type index]
      [edit-rule-service-format index]
      [edit-rule-health-domain index]
      [edit-rule-health-category index]])])

(defn- period-indicator [index]
  (let [start @(rf/subscribe [::start index])
        end @(rf/subscribe [::end index])]
    (cond
      (and start end) (str start " bis " end)
      start (str "ab " start)
      end (str "bis " end)
      :else "immer")))

(rf/reg-event-db ::init-cap-for-rule
  (fn [db [_ rule-index]]
    (-> db
        (assoc-in [:data :rules rule-index :subsidy-cap] 50)
        (assoc-in [:data :rules rule-index :result] 100))))

(rf/reg-event-db ::unset-cap-for-rule
  (fn [db [_ rule-index]]
    (update-in db [:data :rules rule-index] #(dissoc % :subsidy-cap))))

(defn- rule [index]
  (let [#_:clj-kondo/ignore
        rule @(rf/subscribe [::rule-by-index index])
        prev-rule @(rf/subscribe [::rule-by-index (dec index)])
        next-rule @(rf/subscribe [::rule-by-index (inc index)])
        product-id (-> rule :condition :product :id)
        prev-product-id (-> prev-rule :condition :product :id)
        next-product-id (-> next-rule :condition :product :id)
        top-reached? (>= (inc index) @(rf/subscribe [::num-rules]))
        bottom-reached? (= 0 index)
        editing? (= index @(rf/subscribe [:get [:ui :editing :rule]]))
        is_estg3no34_subsidizable? (-> rule :condition :product :is_estg3no34_subsidizable)]
    [:tr {:class ["border-t" "border-gray-100"]}
     [:td {:class (conj styles/td "w-px")}
      [:div {:class ["flex" "items-center" "gap-1"]}
       (when (and (not top-reached?) (= next-product-id product-id))
         [:button {:type "button"
                   :class styles/reorder-button
                   :aria-label "Regel nach unten verschieben"
                   :on-click #(rf/dispatch [::move-rule-down index])}
          [styles/arrow-icon true]])
       (when (and (not bottom-reached?) (= prev-product-id product-id))
         [:button {:type "button"
                   :class styles/reorder-button
                   :aria-label "Regel nach oben verschieben"
                   :on-click #(rf/dispatch [::move-rule-up index])}
          [styles/arrow-icon false]])]]
     [:td {:class (conj styles/td "text-gray-900")}
      (if editing?
        [edit-rule-conditions-employee index]
        [rule-conditions-employee (get-in rule [:condition :employee])])]
     [:td {:class styles/td}
      (if editing?
        [edit-rule-conditions-product index product-id]
        [rule-conditions-product (get-in rule [:condition :product])])]
     [:td {:class styles/td}
      (if (and editing? (nil? product-id))
        [:div {:class ["flex" "flex-col" "gap-1"]}
         [:span "Regel gilt…"]
         [:span {:class ["text-xs" "text-gray-500"]} "… ab einschließlich"]
         [:input {:value @(rf/subscribe [::start index])
                  :type "date"
                  :on-change #(rf/dispatch [::set-start index (-> % .-target .-value)])}]
         [:span {:class ["text-xs" "text-gray-500"]} "… bis einschließlich"]
         [:input {:value @(rf/subscribe [::end index])
                  :type "date"
                  :on-change #(rf/dispatch [::set-end index (-> % .-target .-value)])}]]
        [period-indicator index])]
     [:td {:class styles/td}
      [:div {:class ["flex" "items-center" "gap-1.5"]}
       [widgets/input
        {:value (rule :result)
         :type widgets/type-percent-number
         :on-save #(rf/dispatch [:set-path [:data :rules index :result] %])
         :class percent-input-classes
         :disabled? (boolean (:subsidy-cap rule))}]
       [:span {:class ["text-sm" "text-gray-400"]} "%"]]
      (cond
        (and (not editing?)
             (:subsidy-cap rule))
        [:p {:class ["mt-1.5" "text-xs" "text-gray-500"]}
         "nur als Sachbezug bis " (:subsidy-cap rule) " €/Monat"]
        (and editing? (not is_estg3no34_subsidizable?))
        (if (:subsidy-cap rule)
          [:div {:class ["mt-2" "flex" "items-center" "gap-1.5"]}
           [:span {:class ["text-xs" "text-gray-500"]} "nur als Sachbezug bis"]
           [widgets/input
            {:value (:subsidy-cap rule)
             :type widgets/type-positive-number
             :on-save #(rf/dispatch [:set-path [:data :rules index :subsidy-cap] %])
             :class percent-input-classes
             :max (str legal-cap-for-monthly-sachbezug)
             :min "1"}]
           [:span {:class ["text-xs" "text-gray-500"]} "€/Monat"]
           [:button {:type "button"
                     :class ["text-gray-400" "transition-colors" "hover:text-gray-600"]
                     :aria-label "Sachbezugsgrenze entfernen"
                     :on-click #(rf/dispatch [::unset-cap-for-rule index])} "×"]]
          [:div {:class ["mt-2"]}
           [:button {:type "button"
                     :class action-link-classes
                     :on-click #(rf/dispatch [::init-cap-for-rule index])}
            "Sachbezugsgrenze setzen"]]))]

     [:td {:class (conj styles/td "whitespace-nowrap" "text-right")}
      [:div {:class ["flex" "items-center" "justify-end" "gap-2"]}
       (when-not editing?
         [:button {:type "button"
                   :class styles/edit-action
                   :on-click #(rf/dispatch [::edit-rule index])} "bearbeiten"])
       (when editing?
         [:button {:type "button"
                   :class styles/done-action
                   :on-click #(rf/dispatch [::edit-rule nil])} "fertig"])
       [:button {:type "button"
                 :class styles/delete-action
                 :on-click #(rf/dispatch [::delete-rule index])} "löschen"]]]]))

(defn- rules []
  ;; w-full keeps the card at lane width so the wide edit row scrolls inside the overflow-x-auto
  ;; wrapper below instead of stretching the card (and the page) horizontally (QA #2).
  [:div {:class (conj styles/card "w-full")}
   [:div {:class ["border-b" "border-gray-100" "px-6" "py-4"]}
    [:h2 {:class ["text-base" "font-semibold" "text-gray-900"]}
     "Regeln für die Bestimmung der Fördersätze"]
    [:p {:class ["mt-0.5" "text-xs" "text-gray-500"]}
     "bitte die Reihenfolge beachten, der erste Treffer gilt"]]
   [:div {:class ["overflow-x-auto"]}
    [:table {:class ["w-full" "text-left" "text-sm"]}
     [:thead
      [:tr
       [:th {:class styles/th}]
       [:th {:class styles/th} "Falls Mitarbeiter"]
       [:th {:class styles/th} "und Angebot"]
       [:th {:class styles/th} "und Regel aktuell"]
       [:th {:class styles/th} "dann Fördersatz in %"]
       [:th {:class styles/th}]]]
     (into [:tbody]
           (for [index (range @(rf/subscribe [::num-rules]))]
             ^{:key index}
             [rule index]))]]
   [:div {:class ["border-t" "border-gray-100" "px-6" "py-4"]}
    [:button {:type "button"
              :class styles/ghost-button
              :on-click #(rf/dispatch [::add-rule])}
     "+ Regel hinzufügen"]]])

(rf/reg-event-fx ::save-rules
  (fn [{:keys [db]} _]
    {:post {:url (get-in db [:io :save-url])
            :csrf-token (get-in db [:io :csrf-token])
            :data (clj->js {:rules (-> db :data :rules)})
            :on-success #(rf/dispatch [::save-rules-success %])
            :on-error #(rf/dispatch [::save-rules-error])}}))

(rf/reg-event-fx ::save-rules-success
  (fn [_ [_ result]]
    {:redirect {:url (:redirect-url result)}}))

(rf/reg-event-fx ::save-rules-error
  (fn [_ _]
    {::alert {:msg "Das hat leider nicht geklappt. Bitte wende dich an das Produkt-Team."}}))

(defn- save-button []
  [:button {:type "button"
            :class styles/primary-button
            :on-click #(rf/dispatch [::save-rules])}
   "Speichern"])

(defn subsidy-rate []
  [:div#subsidy_rate
   {:class (into ["flex" "flex-col" "items-start" "gap-5"] edit-control-variant-classes)}
   [rules]
   [save-button]])
